// combatpro/panels/spells/SpellStudioModal.js
//
// Combat PRO - SpellStudioModal (Stage 53: Custom Spell Studio Completion).
//
// The complete React migration of the legacy Custom Spell modal
// (#vi-add-spell-modal + its runtime, battletracker.html 12686-13070 / 13277-13443).
// Stage 52 delivered the shell; this stage adds the full editor: all field
// inputs, the duration controls, guided/quick mode, progress + step locking,
// validation, the create/edit/save workflow, and the runtime refresh after save.
//
// Architecture (unchanged from Stage 52): follows the ConditionsPanel overlay
// pattern. The parent (CombatProEngineView) owns the open state and the active
// editing spell via local useState and passes them as props (isOpen /
// editingSpell / onClose). This component owns only TRANSIENT editor presentation
// state (the in-progress field values, the guided/quick mode, and the error /
// success messages). It reads NO CombatContext and introduces no second modal
// system. CombatContext is unchanged.
//
// Runtime reuse (no duplication, no ownership moved):
//   - spellStudioEngine.js (Stage 50): defaultSpellFields / spellToFields seed the
//     editor; evaluateStepCompletion + canSaveSpell drive progress / locking / save
//     gating; computeDurationRounds drives the read-only Duration Rounds display;
//     validateSpellName performs required + duplicate-name validation;
//     buildCustomSpell constructs the saved spell (id / source / duration / all
//     field transforms). Enumerations come from the same module.
//   - spellPersistence.js (Stage 51): saveUserSpell performs the POST. After a
//     successful save the editor calls props.onSaved (CombatContext.refreshSpellLibrary,
//     Stage 55) which reloads + merges into the runtime spell DB and bumps the
//     library version. No backend call is recreated here, and the runtime refresh
//     is owned by the context, not duplicated in the editor.
//
// The runtime spell DB the rest of the app reads is window.VI_SPELLS_DB (the
// legacy SPELLS_DB; the same source castSpell / SpellSlot read). Duplicate-name
// validation reads it. The post-save reload + selector refresh (the declarative
// vi_rebuildSpellSelects equivalent) is performed by the Stage 55
// CombatContext.refreshSpellLibrary via the onSaved callback.
//
// Visibility: returns null when closed (clean React equivalent of the legacy
// style.display toggle). Close happens via the X and Cancel only (no backdrop
// click), matching the legacy. Field state is reseeded every time the modal opens
// (create -> blank, edit -> populated), which is the editor reset on open/close.
//
// No optional chaining. No em dash (rendered via {'\u2014'}; bullets are literal).
// No DOM, no getElementById, no innerHTML. Do not edit battletracker.html.

import React, { useState } from 'react';
import {
  defaultSpellFields,
  spellToFields,
  buildCustomSpell,
  computeDurationRounds,
  validateSpellName,
  evaluateStepCompletion,
  canSaveSpell,
  SPELL_SCHOOLS,
  SPELL_TYPES,
  SPELL_LEVELS,
  SAVE_TYPES,
  DURATION_TYPES,
  DURATION_UNITS,
  CONCENTRATION_OPTIONS
} from '../../spell/spellStudioEngine';
import { saveUserSpell } from '../../spell/spellPersistence';

// Presentation-only display labels for the few selects whose visible text differs
// from the stored value (the legacy option text). These are display strings, not
// runtime logic; the option VALUES come from the Stage 50 enumerations.
var TYPE_LABELS = { damage: 'Damage', control: 'Control', heal: 'Heal', buff: 'Buff', utility: 'Utility' };
var DURATION_TYPE_LABELS = { instant: 'Instantaneous', timed: 'Timed', concentration: 'Concentration', dispel: 'Until Dispelled', special: 'Special' };
var DURATION_UNIT_LABELS = { rounds: 'Rounds', minutes: 'Minutes', hours: 'Hours', days: 'Days' };

// Inline styles translated verbatim from the legacy modal markup.
var S = {
  overlay: { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.7)', zIndex: 99999, overflowY: 'auto' },
  panel: { background: '#1A2333', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '12px', maxWidth: '1400px', width: '96%', margin: '40px auto', padding: '28px', position: 'relative' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  title: { margin: 0, color: '#F0B429', fontSize: '18px', fontWeight: 800, letterSpacing: '0.5px' },
  headerRight: { display: 'flex', gap: '12px', alignItems: 'center' },
  closeBtn: { background: 'transparent', border: 'none', color: '#9AA4B2', fontSize: '22px', cursor: 'pointer', lineHeight: 1 },
  bannerUser: { display: 'block', background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.4)', color: '#93c5fd', borderRadius: '6px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px', fontWeight: 700 },
  bannerSystem: { display: 'block', background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.4)', color: '#fbbf24', borderRadius: '6px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px', fontWeight: 700 },
  errorBox: { background: 'rgba(220,38,38,0.15)', border: '1px solid rgba(220,38,38,0.4)', color: '#f87171', borderRadius: '6px', padding: '10px 14px', marginBottom: '16px', fontSize: '14px' },
  successBox: { background: 'rgba(22,163,74,0.15)', border: '1px solid rgba(22,163,74,0.4)', color: '#86efac', borderRadius: '6px', padding: '10px 14px', marginBottom: '16px', fontSize: '14px' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '14px', alignItems: 'start' },
  footer: { display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' },
  cancelBtn: { background: '#1E2D42', border: '1px solid rgba(255,255,255,0.12)', color: '#E8EDF3', borderRadius: '6px', padding: '10px 20px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  saveBtn: { background: '#F0B429', border: 'none', color: '#0B0F14', borderRadius: '6px', padding: '10px 24px', fontSize: '14px', fontWeight: 800, fontFamily: 'inherit' }
};

var F = {
  label: { color: '#9AA4B2', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '5px' },
  input: { width: '100%', boxSizing: 'border-box', height: '40px', background: '#0F1A2A', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', color: '#E8EDF3', fontSize: '14px', padding: '0 12px' },
  textarea: { width: '100%', boxSizing: 'border-box', background: '#0F1A2A', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', color: '#E8EDF3', fontSize: '14px', padding: '9px 12px', resize: 'vertical', fontFamily: 'inherit' },
  readonly: { width: '100%', boxSizing: 'border-box', height: '40px', background: '#0A1220', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '6px', color: '#9AA4B2', fontSize: '14px', padding: '0 12px', cursor: 'default' },
  req: { color: '#f87171' },
  spanFull: { gridColumn: 'span 3' },
  span2: { gridColumn: 'span 2' }
};

function SpellStudioModal(props) {
  const [fields, setFields] = useState(function() { return defaultSpellFields(); });
  const [editMode, setEditMode] = useState('guided');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [prevOpen, setPrevOpen] = useState(false);
  const [prevSpell, setPrevSpell] = useState(null);

  // Seed / reset the editor synchronously when it opens (or when the editing spell
  // changes while open), using React's documented "adjust state during render"
  // pattern - so there is no stale first frame and no effect is needed. This is
  // the editor reset on open/close: every open reseeds (create -> blank via
  // defaultSpellFields, edit -> populated via spellToFields), mirroring the legacy
  // vi_openAddSpellModal / vi_loadSpellIntoModal. The latch resets on close so the
  // next open reseeds.
  if (props.isOpen && (!prevOpen || prevSpell !== props.editingSpell)) {
    setPrevOpen(true);
    setPrevSpell(props.editingSpell);
    setFields(props.editingSpell ? spellToFields(props.editingSpell) : defaultSpellFields());
    setEditMode('guided');
    setError('');
    setSuccess('');
  }
  if (!props.isOpen && prevOpen) {
    setPrevOpen(false);
  }

  if (!props.isOpen) { return null; }

  const editingSpell = props.editingSpell || null;

  function setField(key, value) {
    setFields(function(prev) {
      const next = Object.assign({}, prev);
      next[key] = value;
      return next;
    });
  }

  // Derived (no stored progress state - recomputed from fields, as the legacy did
  // on every input via vi_spellProgressTick).
  const progress = evaluateStepCompletion(fields);
  const durationRounds = computeDurationRounds(fields);
  const isGuided = editMode === 'guided';
  const saveDisabled = isGuided && !canSaveSpell(fields);

  // Step container class (vi_updateStepUI). In quick mode steps are never locked.
  function stepClass(s) {
    if (!isGuided) { return 'spell-step'; }
    if (progress[s]) { return 'spell-step complete'; }
    if (s === 1 || progress[s - 1]) { return 'spell-step active'; }
    return 'spell-step locked';
  }

  // Progress indicator class (vi_updateProgressBar).
  function indClass(s) {
    if (progress[s]) { return 'complete'; }
    if (s === 1 || progress[s - 1]) { return 'active'; }
    return '';
  }

  // Banner (vi_loadSpellIntoModal): create -> none; user -> blue; system -> gold.
  let banner = null;
  if (editingSpell) {
    if (editingSpell.source === 'user') {
      banner = <div style={S.bannerUser}>Editing Custom Spell - Changes will overwrite existing version.</div>;
    } else {
      banner = <div style={S.bannerSystem}>Editing System Spell - Will be saved as a NEW custom spell.</div>;
    }
  }

  // Save workflow (vi_saveCustomSpell): validate -> build -> POST -> refresh ->
  // success / close, reusing the Stage 50 + Stage 51 runtime end to end.
  async function handleSave() {
    setError('');
    setSuccess('');

    const name = (fields.name || '').trim();
    const spellsDb = (typeof window !== 'undefined' && window.VI_SPELLS_DB) ? window.VI_SPELLS_DB : [];
    const editingId = editingSpell ? editingSpell.id : null;

    const nameCheck = validateSpellName(name, spellsDb, editingId);
    if (!nameCheck.valid) {
      setError(nameCheck.error);
      return;
    }

    const spellData = buildCustomSpell(fields, { editingSpell: editingSpell });

    const saved = await saveUserSpell(spellData);
    if (!saved) {
      setError('Save failed. Check your connection and try again.');
      return;
    }

    // Stage 55 (Spell Library Integration): refresh the spell library app-wide via
    // the centralised CombatContext.refreshSpellLibrary, passed in as onSaved. It
    // reloads + merges into the runtime spell DB (window.VI_SPELLS_DB) and bumps the
    // library version so every dropdown / consumer rebuilds from the refreshed DB
    // (the declarative vi_rebuildSpellSelects equivalent). The runtime refresh is
    // owned there, not duplicated here; the editor only validates, builds, and saves.
    if (props.onSaved) { await props.onSaved(); }

    const isEditingUser = editingSpell && editingSpell.source === 'user';
    setSuccess('"' + name + '" ' + (isEditingUser ? 'updated.' : 'saved and added to spell dropdowns.'));
    setTimeout(function() { props.onClose(); }, 1200);
  }

  function helper(title, body) {
    if (!isGuided) { return null; }
    return (
      <div className="step-helper">
        <div className="helper-title">{title}</div>
        <div className="helper-body">{body}</div>
      </div>
    );
  }

  return (
    <div id="vi-add-spell-modal" style={S.overlay}>
      <div style={S.panel}>
        <div style={S.headerRow}>
          <h2 style={S.title}>{'\u2728'} Create Custom Spell</h2>
          <div style={S.headerRight}>
            <div className="vi-edit-mode-toggle">
              <button className={isGuided ? 'active' : ''} onClick={function() { setEditMode('guided'); }}>Guided</button>
              <button className={!isGuided ? 'active' : ''} onClick={function() { setEditMode('quick'); }}>Quick Edit</button>
            </div>
            <button onClick={props.onClose} style={S.closeBtn}>{'\u2715'}</button>
          </div>
        </div>

        {error ? <div style={S.errorBox}>{error}</div> : null}
        {success ? <div style={S.successBox}>{success}</div> : null}
        {banner}

        <div className="spell-progress">
          <div data-step-indicator="1" className={indClass(1)}>Core</div>
          <div data-step-indicator="2" className={indClass(2)}>Casting</div>
          <div data-step-indicator="3" className={indClass(3)}>Effect</div>
          <div data-step-indicator="4" className={indClass(4)}>Outcome</div>
          <div data-step-indicator="5" className={indClass(5)}>Description</div>
        </div>

        <div style={S.grid}>

          {/* Step 1 - Core */}
          <div className={stepClass(1)} data-step="1">
            {helper('Define Your Spell Core', (
              <span>
                Set the foundation of your spell.<br /><br />
                {'\u2022'} <strong>Spell Name</strong> {'\u2014'} unique identifier for selection and logs<br />
                {'\u2022'} <strong>Level</strong> {'\u2014'} determines power scaling<br />
                {'\u2022'} <strong>School</strong> {'\u2014'} defines spell category and interactions<br />
                {'\u2022'} <strong>Spell Type</strong> {'\u2014'} controls how the engine executes the spell
              </span>
            ))}
            <div style={F.spanFull}>
              <label style={F.label}>Spell Name <span style={F.req}>*</span></label>
              <input type="text" id="vi-spell-name" placeholder="e.g. Chromatic Orb" style={F.input} value={fields.name} onChange={function(e) { setField('name', e.target.value); }} />
            </div>
            <div>
              <label style={F.label}>Level <span style={F.req}>*</span></label>
              <select id="vi-spell-level" style={F.input} value={fields.level} onChange={function(e) { setField('level', e.target.value); }}>
                {SPELL_LEVELS.map(function(l) { return <option key={l} value={String(l)}>{l === 0 ? '0 (Cantrip)' : String(l)}</option>; })}
              </select>
            </div>
            <div>
              <label style={F.label}>School</label>
              <select id="vi-spell-school" style={F.input} value={fields.school} onChange={function(e) { setField('school', e.target.value); }}>
                {SPELL_SCHOOLS.map(function(s) { return <option key={s} value={s}>{s}</option>; })}
              </select>
            </div>
            <div>
              <label style={F.label}>Spell Type <span style={F.req}>*</span></label>
              <select id="vi-spell-type" style={F.input} value={fields.spellType} onChange={function(e) { setField('spellType', e.target.value); }}>
                {SPELL_TYPES.map(function(t) { return <option key={t} value={t}>{TYPE_LABELS[t] || t}</option>; })}
              </select>
            </div>
          </div>

          {/* Step 2 - Casting */}
          <div className={stepClass(2)} data-step="2">
            {helper('Define Casting Behaviour', (
              <span>
                Control how and when the spell is used.<br /><br />
                {'\u2022'} <strong>Casting Time</strong> {'\u2014'} when it can be executed in combat<br />
                {'\u2022'} <strong>Range</strong> {'\u2014'} how far the spell can reach<br />
                {'\u2022'} <strong>Components</strong> {'\u2014'} required casting elements<br />
                {'\u2022'} <strong>Duration Type</strong> {'\u2014'} instant or sustained effect
              </span>
            ))}
            <div>
              <label style={F.label}>Casting Time</label>
              <input type="text" id="vi-spell-castingTime" placeholder="1 action" style={F.input} value={fields.castingTime} onChange={function(e) { setField('castingTime', e.target.value); }} />
            </div>
            <div>
              <label style={F.label}>Range</label>
              <input type="text" id="vi-spell-range" placeholder="60 feet" style={F.input} value={fields.range} onChange={function(e) { setField('range', e.target.value); }} />
            </div>
            <div>
              <label style={F.label}>Duration Type</label>
              <select id="durationType" style={F.input} value={fields.durationType} onChange={function(e) { setField('durationType', e.target.value); }}>
                {DURATION_TYPES.map(function(d) { return <option key={d} value={d}>{DURATION_TYPE_LABELS[d] || d}</option>; })}
              </select>
            </div>

            {fields.durationType === 'timed' ? (
              <div id="vi-dur-unit-wrap">
                <label style={F.label}>Duration Unit</label>
                <select id="durationUnit" style={F.input} value={fields.durationUnit} onChange={function(e) { setField('durationUnit', e.target.value); }}>
                  {DURATION_UNITS.map(function(u) { return <option key={u} value={u}>{DURATION_UNIT_LABELS[u] || u}</option>; })}
                </select>
              </div>
            ) : null}
            {fields.durationType === 'timed' ? (
              <div id="vi-dur-value-wrap">
                <label style={F.label}>Duration Value</label>
                <input type="number" id="durationValue" min="0" style={F.input} value={fields.durationValue} onChange={function(e) { setField('durationValue', e.target.value); }} />
              </div>
            ) : null}
            {fields.durationType === 'concentration' ? (
              <div id="vi-dur-conc-wrap">
                <label style={F.label}>Concentration Duration</label>
                <select id="durationConcSelect" style={F.input} value={fields.durationConcSelect} onChange={function(e) { setField('durationConcSelect', e.target.value); }}>
                  {CONCENTRATION_OPTIONS.map(function(o) { return <option key={o.value} value={String(o.value)}>{o.label}</option>; })}
                </select>
              </div>
            ) : null}
            {fields.durationType === 'special' ? (
              <div id="vi-dur-special-wrap">
                <label style={F.label}>Special Duration Text</label>
                <input type="text" id="durationSpecialText" placeholder="Describe duration..." style={F.input} value={fields.durationSpecialText} onChange={function(e) { setField('durationSpecialText', e.target.value); }} />
              </div>
            ) : null}

            <div>
              <label style={F.label}>Components</label>
              <input type="text" id="vi-spell-components" placeholder="V, S, M" style={F.input} value={fields.components} onChange={function(e) { setField('components', e.target.value); }} />
            </div>
          </div>

          {/* Step 3 - Effect */}
          <div className={stepClass(3)} data-step="3">
            {helper('Define Target Interaction', (
              <span>
                Control how targets are affected.<br /><br />
                {'\u2022'} <strong>Save Type</strong> {'\u2014'} how targets resist<br />
                {'\u2022'} <strong>Save Effect</strong> {'\u2014'} what happens on success<br />
                {'\u2022'} <strong>Area of Effect</strong> {'\u2014'} multi-target coverage<br />
                {'\u2022'} <strong>Applies Condition</strong> {'\u2014'} status effects applied
              </span>
            ))}
            <div>
              <label style={F.label}>Save Type</label>
              <select id="vi-spell-saveType" style={F.input} value={fields.saveType} onChange={function(e) { setField('saveType', e.target.value); }}>
                {SAVE_TYPES.map(function(t) { return <option key={t === '' ? 'none' : t} value={t}>{t === '' ? 'None' : t}</option>; })}
              </select>
            </div>
            <div>
              <label style={F.label}>Save Effect</label>
              <input type="text" id="vi-spell-saveEffect" placeholder="half / none" style={F.input} value={fields.saveEffect} onChange={function(e) { setField('saveEffect', e.target.value); }} />
            </div>
            <div>
              <label style={F.label}>Area of Effect</label>
              <input type="text" id="vi-spell-areaOfEffect" placeholder="e.g. 20ft-radius" style={F.input} value={fields.areaOfEffect} onChange={function(e) { setField('areaOfEffect', e.target.value); }} />
            </div>
            <div>
              <label style={F.label}>Applies Condition</label>
              <input type="text" id="vi-spell-appliesCondition" placeholder="e.g. frightened" style={F.input} value={fields.appliesCondition} onChange={function(e) { setField('appliesCondition', e.target.value); }} />
            </div>
          </div>

          {/* Step 4 - Outcome */}
          <div className={stepClass(4)} data-step="4">
            {helper('Define Execution Result', (
              <span>
                Define the actual impact of the spell.<br /><br />
                {'\u2022'} <strong>Damage Formula</strong> {'\u2014'} core damage calculation<br />
                {'\u2022'} <strong>Duration Rounds</strong> {'\u2014'} how long effects persist
              </span>
            ))}
            <div>
              <label style={F.label}>Duration Rounds <span style={{ color: '#9AA4B2', fontWeight: 400 }}>(computed)</span></label>
              <input type="number" id="vi-spell-durationRounds" value={String(durationRounds)} min="0" readOnly style={F.readonly} />
            </div>
            <div style={F.span2}>
              <label style={F.label}>Damage Formula</label>
              <input type="text" id="vi-spell-damageFormula" placeholder="e.g. 3d6 or 8d8+4" style={F.input} value={fields.damageFormula} onChange={function(e) { setField('damageFormula', e.target.value); }} />
            </div>
            <div></div>
          </div>

          {/* Step 5 - Description */}
          <div className={stepClass(5)} data-step="5">
            {helper('Finalize Spell Description', (
              <span>
                Provide a clear readable explanation.<br /><br />
                {'\u2022'} Used for reference during combat<br />
                {'\u2022'} Helps players understand effects quickly<br />
                {'\u2022'} Should match actual behaviour defined above
              </span>
            ))}
            <div style={F.spanFull}>
              <label style={F.label}>Description</label>
              <textarea id="vi-spell-desc" placeholder="Spell description..." rows="3" style={F.textarea} value={fields.desc} onChange={function(e) { setField('desc', e.target.value); }} />
            </div>
          </div>

        </div>

        <div style={S.footer}>
          <button onClick={props.onClose} style={S.cancelBtn}>Cancel</button>
          <button onClick={handleSave} disabled={saveDisabled} style={Object.assign({}, S.saveBtn, { cursor: saveDisabled ? 'not-allowed' : 'pointer', opacity: saveDisabled ? 0.5 : 1 })}>Save Spell</button>
        </div>
      </div>
    </div>
  );
}

export default SpellStudioModal;
